import { useTranslation } from 'react-i18next';
import { PlatformIcon } from '@/components/PlatformIcon';
import type { CollectionBase } from '@/features/events/types';
import {
  getAdminConsentUrl,
  getAuthUrl,
} from '@/features/interactions/addToOutlook/auth.lib';
import { useOutlookConsentStatus } from '@/features/interactions/addToOutlook/hooks/useOutlookConsentStatus';
import type { LaunchOption } from '@/hooks/useLauncher';
import { useIntegrations } from '@/pages/Schedule/hooks/useIntegrations';
import { IntegrationType, PlatformTypes } from '@/types/gql.generated';
import { Button, Flex, Text } from '@/ui';

type Props = {
  launchOnRedirect?: LaunchOption;
  email?: string;
  // Magic collection context — carries the job id in the redirect so the magic
  // page reloads its events (and the launch-resume handler) after the OAuth trip.
  base?: CollectionBase | null;
};

export const OutlookCalendarAuthButton = ({
  launchOnRedirect,
  email,
  base,
}: Props): JSX.Element => {
  const { t } = useTranslation('settingsModal');
  const { data = [] } = useIntegrations();
  const { isAdminConsentRequired, isAdminConsentGranted } =
    useOutlookConsentStatus();
  const integrations = data.filter(
    (item) => item.type === IntegrationType.OutlookCalendar
  );

  const handleAuth = () => {
    const query: Record<string, string> = {};
    if (launchOnRedirect) {
      query.launch = launchOnRedirect;
    }
    window.location.href = getAuthUrl(query, { email }, base);
  };

  const handleAdminConsent = () => {
    window.location.href = getAdminConsentUrl(base);
  };

  return (
    // Stretch rather than center so the button matches the width of whatever
    // container it is placed in — that keeps it flush with the Google button
    // when the two are stacked. Call sites that want it centered wrap it
    // themselves; the messages below stay centered on their own.
    <Flex direction="column" gap="3">
      <Button
        bgColor="#FFF"
        border="1px solid #dadce0"
        borderRadius="full"
        color="#3c4043"
        display="flex"
        fontSize="14px"
        fontWeight="500"
        h={38}
        justifyContent="center"
        letterSpacing="0.25px"
        overflow="hidden"
        p="0"
        pos="relative"
        px="4"
        transition="background-color .218s"
        type="button"
        _hover={{
          borderColor: '#d2e3fc',
          backgroundColor: 'rgba(0, 120, 212, .04)',
        }}
        onClick={handleAuth}
      >
        <Flex align="center" gap="2" pos="relative">
          <PlatformIcon
            platform={PlatformTypes.OutlookIntegration}
            size="18px"
          />
          <Text>
            {integrations.length > 0
              ? t('calendars.connect_outlook_another')
              : t('calendars.connect_outlook')}
          </Text>
        </Flex>
      </Button>

      {isAdminConsentGranted && (
        <Text color="green.600" fontSize="sm" textAlign="center">
          Your organization approved Agenda Hero — you can now connect.
        </Text>
      )}

      {isAdminConsentRequired && (
        <Flex align="center" direction="column" gap="1">
          <Text color="customgray.mid" fontSize="sm" textAlign="center">
            Your organization requires an administrator to approve Agenda Hero
            before you can connect Outlook.
          </Text>
          <Button
            colorScheme="dark"
            fontSize="sm"
            textDecor="underline"
            variant="link"
            onClick={handleAdminConsent}
          >
            Get admin approval
          </Button>
        </Flex>
      )}
    </Flex>
  );
};
